// Apple 계정 연동 해제 다이얼로그 상태 관리 훅
import { useState, useCallback } from 'react';

/**
 * 🎧 웨어러블 기기 데이터 타입 확장
 * - 기본 기기 정보 + 가격 정보 (estimatedPrice, allGradePrices) 포함
 */
interface WearableDeviceData {
  deviceType: string;
  deviceModel: string;
  // 🔥 가격 정보 추가 (웨어러블 구성에 해당하는 가격)
  estimatedPrice?: number | null;
  allGradePrices?: { A: number; B: number; C: number; D: number; E: number } | null;
  // 선택된 구성 등급 (A=풀세트, B=유닛세트, C=유닛1쪽, D=본체만)
  userSelectedGrade?: string;
}

export const useAppleAccountDialog = () => {
  const [showAppleUnlinkDialog, setShowAppleUnlinkDialog] = useState(false);
  // 🔥 타입 확장: 가격 정보도 저장 가능
  const [pendingDeviceData, setPendingDeviceData] = useState<WearableDeviceData | null>(null);

  // 웨어러블 기기 추가 전 애플 계정 연동 확인 (기존 호환성 유지)
  // isQuoteMode: 시세조회 모드 여부
  const createHandleWearableDeviceAdd = useCallback((
    onAdd: ((data: any) => void) | undefined,
    onClose: () => void,
    isQuoteMode: boolean = false
  ) => {
    // 🔥 타입 확장: 가격 정보도 전달 가능
    return (deviceData: WearableDeviceData) => {
      if (deviceData.deviceType === '웨어러블') {
        setPendingDeviceData(deviceData);
        setShowAppleUnlinkDialog(true);
      } else {
        // 시세조회 모드가 아니고 onAdd가 있을 때만 기기 추가
        if (!isQuoteMode && onAdd) {
          onAdd(deviceData);
        }
        onClose();
      }
    };
  }, []);

  // 애플 계정 연동 해제 확인 후 기기 추가 (기존 호환성 유지)
  // isQuoteMode: 시세조회 모드일 때는 다음 단계(시세 확인)로 이동
  // goToNextStep: 시세조회 모드일 때 다음 단계로 이동하는 콜백
  const createHandleAppleUnlinkConfirm = useCallback((
    onAdd: ((data: any) => void) | undefined,
    onClose: () => void,
    isQuoteMode: boolean = false,
    goToNextStep?: () => void
  ) => {
    return () => {
      if (pendingDeviceData) {
        if (isQuoteMode && goToNextStep) {
          // 🎯 시세조회 모드: 다음 단계(시세 확인)로 이동
          setShowAppleUnlinkDialog(false);
          goToNextStep();
        } else {
          // 기존 모드: onAdd 호출
          if (onAdd) {
            onAdd(pendingDeviceData);
          }
          setShowAppleUnlinkDialog(false);
          setPendingDeviceData(null);
          onClose();
        }
      }
    };
  }, [pendingDeviceData]);

  // 애플 계정 연동 해제 다이얼로그 취소
  const handleAppleUnlinkCancel = useCallback(() => {
    setShowAppleUnlinkDialog(false);
    setPendingDeviceData(null);
  }, []);

  return {
    showAppleUnlinkDialog,
    pendingDeviceData,
    setShowAppleUnlinkDialog,
    setPendingDeviceData,
    createHandleWearableDeviceAdd,
    createHandleAppleUnlinkConfirm,
    handleAppleUnlinkCancel
  };
};
